import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQuery } from "../../AppLayout";
import AppBreadcrumb from "../../AppBreadcrumb";
import { SmallLoading } from "../Loading";
import TaskAction from "../../actions/TaskAction";
import { ActivityTypes, ContentTypes, DialogTypes } from "../../constants/action-types";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import FieldNav from "../../fields/FieldNav";
import SettingNav, { workspaceSettingNavItems } from "../../components/SettingNav";
import ActivityAction from "../../actions/ActivityAction";
import DashboardAction from "../../actions/DashboardAction";
import LayoutAction from "../../actions/LayoutAction";
import {
  CalendarIcon,
  InboxIcon,
  PlusIcon,
  UserIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import {
  LayoutGrid,
  Edit3,
  Plus as LucidePlus,
  Save as LucideSave,
  CalendarDays,
  Phone,
  Flag,
  Clock,
  NotepadText,
  Mail,
  CalendarRange,
  Grid2X2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import DropdownLayout from "../../components/DropdownLayout";
import Button from "../../components/ui/Button";
import ReportAction from "../../actions/ReportAction";
import ReportSettingAction from "../../actions/ReportSettingAction";
import { fromJS } from "immutable";
import { isGmailContext } from "../../api/api";

const TopNav = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { itemId = "", dashboardId = "", reportId = "" } = useParams();
  const currentParams = useQuery();
  const { t } = useTranslation();

  const projectView = currentParams.get("view");
  const contentType = currentParams.get("type") || "";
  const appView = currentParams.get("key") || "";

  const reportType = currentParams.get("reportType") || "";
  const reportChartType = currentParams.get("reportChartType") || "";
  const reportObjectType = currentParams.get("reportObjectType") || "SALE";
  const reportViewMode = currentParams.get("reportViewMode") || "";

  const viewModeEnabled = reportViewMode === "true";

  const currentUser = useSelector((state: any) => state.userReducer.get("currentUser"));
  const tenantInfo = useSelector((state: any) => state.tenant.get("tenantInfo"));
  const isJira = tenantInfo.getIn(["host", "product"]) === "JIRA";
  const project = useSelector((state: any) => state.projectReducer.get("project"));
  const pending = useSelector((state: any) => state.taskReducer.get("pending"));
  const item = useSelector((state: any) => state.taskReducer.getIn(["task", contentType]));
  const overDueNum = useSelector((state: any) => state.activityReducer.get("myOverDueActivityNum"));
  const reports = useSelector((state: any) => state.report.get("reports")) || fromJS([]);
  const reportData = useSelector((state: any) => state.reportSetting.get("data"));

  useEffect(() => {
    if (reportId === "") {
      dispatch(ReportSettingAction.resetSettingDataReport());
    }
  }, [reportId]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderLoading = () => {
    if (pending) {
      return (
        <div className={"p-3 h-4"}>
          <SmallLoading />
        </div>
      );
    }
    return null;
  };

  const buildItemForActivity = () => {
    if (itemId != "") {
      return {
        value: item.get("id"),
        label: item.getIn(["data", "name", "value"]),
        type: item.get("type"),
      };
    }
    return {};
  };

  const handleAddNew = (type: string) => {
    if (type === "ACTIVITY") {
      dispatch(
        ActivityAction.showActivityDialog(itemId, null, {
          item: buildItemForActivity(),
        })
      );
    } else {
      dispatch(
        TaskAction.showTaskDialog(
          {
            projectId: project.get("id"),
            tenantId: project.get("tenantId"),
            type: type,
            projectView,
          },
          type
        )
      );
    }
  };

  const renderAddDropdown = () => {
    const addOptions = [
      {
        type: "contact",
        label: t("Contact"),
        icon: UserIcon,
        abbreviation: "P",
        contentType: ContentTypes.CONTACT,
      },
      {
        type: "company",
        label: t("Account"),
        icon: BuildingOfficeIcon,
        abbreviation: "O",
        contentType: ContentTypes.COMPANY,
      },
      {
        type: "activity",
        label: t("Activity"),
        icon: CalendarIcon,
        abbreviation: "A",
        contentType: "ACTIVITY",
      },
      {
        type: "sale",
        label: t("Deal"),
        icon: CurrencyDollarIcon,
        abbreviation: "D",
        contentType: ContentTypes.SALE,
      },
    ];

    return (
      <div className="min-w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2">
        {addOptions.map((option) => {
          const IconComponent = option.icon;
          return (
            <div
              key={option.type}
              onClick={() => handleAddNew(option.contentType)}
              data-testid={`add-${option.type.toLowerCase()}-option`}
              className="flex items-center space-x-3 px-4 py-2 hover:bg-gray-50 cursor-pointer"
            >
              <div className="flex-shrink-0">
                <IconComponent className="w-5 h-5 text-gray-700" />
              </div>
              <div className="flex-1">
                <span className="text-sm font-medium text-gray-900">{option.label}</span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderAddNewItem = () => {
    if (contentType == ContentTypes.ACTIVITY || contentType == "") {
      return null;
    }
    return (
      <>
        <div
          className="inline-flex items-center gap-x-1 cursor-pointer hover:bg-gray-50 rounded px-2 py-1 transition-colors flex-shrink-0"
          data-testid="add-new-item-button"
          onClick={() => {
            dispatch(
              TaskAction.showTaskDialog(
                {
                  projectId: project.get("id"),
                  tenantId: project.get("tenantId"),
                  type: contentType,
                  projectView,
                },
                contentType
              )
            );
          }}
        >
          <PlusIcon className="w-4 h-4 flex-shrink-0" />
          <div className="whitespace-nowrap text-sm">{t("Add New")}</div>
        </div>
        <div className="border-r-2 border-gray-200 h-5 hidden sm:block flex-shrink-0" />
      </>
    );
  };

  const renderNewActivityButton = () => {
    if (projectView == "CHART") {
      return null;
    }
    const handleNewActivity = (activityType, summary) => {
      dispatch(
        ActivityAction.showActivityDialog(itemId, null, {
          item: buildItemForActivity(),
          activityType,
          summary,
        })
      );
    };
    return (
      <>
        <Menu as="div" className="relative inline-block flex-shrink-0">
          <MenuButton className="flex items-center gap-x-1 cursor-pointer hover:bg-gray-50 rounded px-2 py-1 transition-colors flex-shrink-0">
            <CalendarIcon className="w-4 h-4 flex-shrink-0" />
            <span>{t("New Activity")}</span>
          </MenuButton>
          <MenuItems className="absolute left-0 z-20 w-max py-2 origin-top-left bg-white shadow-xl rounded ring-opacity-5 transition focus:outline-hidden data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-100 data-leave:duration-75 data-enter:ease-out data-leave:ease-in">
            <MenuItem>
              <button
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-100"
                onClick={() => handleNewActivity(ActivityTypes.meeting, "Meeting")}
              >
                <CalendarDays className="w-4 h-4" />
                <span>{t("Meeting")}</span>
              </button>
            </MenuItem>
            <MenuItem>
              <button
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-100"
                onClick={() => handleNewActivity(ActivityTypes.call, "Call")}
              >
                <Phone className="w-4 h-4" />
                <span>{t("Call")}</span>
              </button>
            </MenuItem>
            <MenuItem>
              <button
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-100"
                onClick={() => handleNewActivity(ActivityTypes.deadline, "Deadline")}
              >
                <Flag className="w-4 h-4" />
                <span>{t("Deadline")}</span>
              </button>
            </MenuItem>
            <MenuItem>
              <button
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-100"
                onClick={() => handleNewActivity(ActivityTypes.task, "")}
              >
                <Clock className="w-4 h-4" />
                <span>{t("Task")}</span>
              </button>
            </MenuItem>
            <MenuItem>
              <button
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-100"
                onClick={() => handleNewActivity(ActivityTypes.commented, "Note")}
              >
                <NotepadText className="w-4 h-4" />
                <span>{t("Note")}</span>
              </button>
            </MenuItem>
            <MenuItem>
              <button
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-100"
                onClick={() => handleNewActivity(ActivityTypes.sendEmail, "")}
              >
                <Mail className="w-4 h-4" />
                <span>{t("Compose Email")}</span>
              </button>
            </MenuItem>
          </MenuItems>
        </Menu>
        <div className="border-r-2 border-gray-200 h-5 hidden sm:block flex-shrink-0" />
      </>
    );
  };

  const renderAddButton = () => {
    if (contentType == ContentTypes.ACTIVITY || contentType == "") {
      return null;
    }
    return (
      <>
        <DropdownLayout
          activator={
            <div
              className="inline-flex items-center gap-x-1 cursor-pointer hover:bg-gray-50 rounded px-2 py-1 transition-colors"
              data-testid="add-new-item-button-dropdown"
            >
              <PlusIcon className="w-4 h-4" />
              <div className="whitespace-nowrap text-sm">{t("Add New")}</div>
              <ChevronDownIcon className="w-3 h-3" />
            </div>
          }
          positionClassName="right-0 origin-top-right"
          fullWidth={false}
        >
          {renderAddDropdown()}
        </DropdownLayout>
        <div className="border-r-2 border-gray-200 h-5 hidden sm:block" />
      </>
    );
  };

  const renderInboxButton = () => {
    if (projectView == "CHART") {
      return null;
    }
    return (
      <div
        className="inline-flex items-center gap-x-1 cursor-pointer hover:bg-gray-50 rounded px-2 py-1 transition-colors flex-shrink-0"
        data-testid="inbox-button"
        onClick={() => {
          dispatch(
            TaskAction.showRightPanelDialog(
              {
                projectId: project.get("id"),
                tenantId: project.get("tenantId"),
                type: contentType,
                projectView,
              },
              DialogTypes.sideMyInboxDialog
            )
          );
        }}
      >
        <InboxIcon className="w-4 h-4 flex-shrink-0" />
        <div className="flex space-x-1 items-center relative w-full justify-end pr-3">
          <div
            title="Overdue activities"
            className="absolute text-green-600 right-0 ml-auto w-9 min-w-max whitespace-nowrap rounded-full bg-white px-2.5 py-0.5 text-center text-xs font-medium leading-5 ring-1 ring-inset ring-gray-200"
          >
            {overDueNum}
          </div>
        </div>
      </div>
    );
  };

  const renderUserProfile = () => {
    if (isJira || isGmailContext()) {
      return null;
    }
    return (
      <div
        className="pr-2 cursor-pointer"
        title={currentUser.get("displayName")}
        onClick={() => {
          navigate(
            `/workspaces/${project.get("id")}/settings/user?item=profile&key=User%20Settings`
          );
        }}
      >
        <img
          src={currentUser.get("avatarUrl")}
          alt={currentUser.get("displayName")}
          className="w-7 h-7 rounded-full object-cover"
        />
      </div>
    );
  };

  const renderReportAction = () => {
    if (reportId !== "") {
      const isNewReport = reportId === "new";
      return (
        <>
          <Button
            variant="text"
            size="sm"
            onClick={() => {
              navigate(
                `/workspaces/${project.get("id")}/reports/dashboard/${dashboardId}/${reportId}?key=Reports&reportType=${reportType || ""
                }&reportChartType=${reportChartType || ""}&reportObjectType=${reportObjectType || "SALE"
                }&reportViewMode=${!viewModeEnabled}`
              );
            }}
            leftIcon={
              !viewModeEnabled ? (
                <LayoutGrid className="w-[14px] h-[14px]" />
              ) : (
                <Edit3 className="w-[14px] h-[14px]" />
              )
            }
          >
            {!viewModeEnabled ? t("View Mode") : t("Edit Mode")}
          </Button>
          {isNewReport ? (
            <>
              <div className="w-px h-5 bg-gray-200" />
              <Button
                variant="text"
                size="sm"
                onClick={async () => {
                  try {
                    const { payload: savedReport } = await dispatch(
                      ReportAction.saveReport(project.get("id"), {
                        type: reportType,
                        data: reportData.toJS(),
                      })
                    );
                    dispatch(
                      LayoutAction.setNotification({
                        title: "Report",
                        type: "success",
                        message: "Report saved successfully",
                      })
                    );
                    if (savedReport && savedReport.id) {
                      await dispatch(
                        DashboardAction.addWidget(project.get("id"), dashboardId, {
                          widgetType: savedReport.type,
                          widgetId: savedReport.id,
                          reportId: savedReport.id,
                          data: {
                            title: savedReport.data?.title || "",
                          },
                          w: 4,
                          h: 9,
                          x: 0,
                          y: 0,
                        })
                      );
                      dispatch(DashboardAction.getDashboards(project.get("id")));
                      dispatch(ReportAction.getReports(project.get("id")));
                      navigate(
                        `/workspaces/${project.get("id")}/reports/dashboard/${dashboardId}/${savedReport.id}?key=Reports&reportType=${reportType || ""
                        }&reportChartType=${reportChartType || ""}&reportObjectType=${reportObjectType || "SALE"
                        }&reportViewMode=${viewModeEnabled}`
                      );
                    }
                  } catch (error) {
                    dispatch(
                      LayoutAction.setNotification({
                        title: "Error",
                        type: "error",
                        message: "Failed to save report",
                      })
                    );
                    throw error;
                  }
                }}
                leftIcon={<LucidePlus className="w-[14px] h-[14px]" />}
              >
                {t("Create Report")}
              </Button>
            </>
          ) : null}
          <div className="w-px h-5 bg-gray-200" />
          <Button
            variant="text"
            size="sm"
            onClick={async () => {
              if (!isNewReport) {
                const report = reports.find((widget: any) => widget.get("id") === reportId);
                if (report) {
                  try {
                    await dispatch(
                      ReportAction.saveReport(project.get("id"), {
                        ...report.toJS(),
                        type: reportType,
                        data: reportData.toJS(),
                      })
                    );
                    dispatch(
                      LayoutAction.setNotification({
                        title: "Report",
                        type: "success",
                        message: "Report saved successfully",
                      })
                    );
                  } catch (error) {
                    dispatch(
                      LayoutAction.setNotification({
                        title: "Error",
                        type: "error",
                        message: "Failed to save report",
                      })
                    );
                    throw error;
                  }
                } else {
                  dispatch(
                    LayoutAction.setNotification({
                      title: t("Error"),
                      type: "error",
                      message: t("Cannot save report: Report ID not found"),
                    })
                  );
                }
              }
              dispatch(ReportSettingAction.resetSettingDataReport());
              dispatch(ReportAction.getReports(project.get("id")));
              navigate(
                `/workspaces/${project.get("id")}/reports/dashboard/${dashboardId}?key=Reports`
              );
            }}
            leftIcon={<LucideSave className="w-[14px] h-[14px]" />}
          >
            {isNewReport ? t("Close") : t("Save & Close")}
          </Button>
        </>
      );
    }
    return null;
  };

  const renderActions = () => {
    switch (appView) {
      case "Activities":
        return (
          <>
            {renderLoading()}
            {/* Mobile: Show Add New dropdown and New Activity button */}
            <div className="flex items-center space-x-2 md:hidden flex-shrink-0">
              {renderAddButton()}
              {renderNewActivityButton()}
              {renderInboxButton()}
              {renderUserProfile()}
            </div>
            {/* Desktop: Show separate Add New and New Activity buttons */}
            <div className="hidden md:flex items-center space-x-2 flex-shrink-0">
              {renderAddNewItem()}
              {renderNewActivityButton()}
              {renderInboxButton()}
              {renderUserProfile()}
            </div>
          </>
        );
      case "Deals":
      case "Accounts":
      case "Contacts":
        return (
          <>
            {renderLoading()}
            {/* Mobile: Show Add New dropdown */}
            <div className="flex items-center space-x-2 md:hidden flex-shrink-0">
              {renderAddButton()}
              {renderInboxButton()}
              {renderUserProfile()}
            </div>
            {/* Desktop: Show separate Add New and New Activity buttons */}
            <div className="hidden md:flex items-center space-x-2 flex-shrink-0">
              {renderAddNewItem()}
              {renderNewActivityButton()}
              {renderInboxButton()}
              {renderUserProfile()}
            </div>
          </>
        );
      case "Content studio": {
        const contentView = currentParams.get("contentView") || "calendar";
        const switchView = (mode: string) => {
          const params = new URLSearchParams(currentParams.toString());
          params.set("contentView", mode);
          navigate({ search: params.toString() });
        };
        return (
          <div className="flex items-center gap-2">
            <div className="w-px h-5 bg-gray-200 flex-shrink-0" />
            <div className="flex items-center bg-gray-100 border border-gray-200 rounded-lg p-[3px] gap-[2px]">
              {[
                { value: "calendar", label: t("Planner") },
                { value: "media", label: t("Media") },
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => switchView(opt.value)}
                  className={`flex items-center gap-1.5 px-2.5 h-7 rounded-md transition-all duration-150 text-xs font-medium ${contentView === opt.value
                    ? "bg-white shadow-sm text-gray-800"
                    : "text-gray-500 hover:text-gray-700"
                    }`}
                >
                  {opt.value === "calendar" ? (
                    <CalendarRange size={13} strokeWidth={1.8} />
                  ) : (
                    <Grid2X2 size={13} strokeWidth={1.8} />
                  )}
                  <span>{opt.label}</span>
                </button>
              ))}
            </div>
            {renderInboxButton()}
            {renderUserProfile()}
          </div>
        );
      }
      case "Reports":
        return <div className="flex items-center space-x-2">{renderReportAction()}</div>;
      case "DataFields":
        return <FieldNav />;
      case "Goals":
        return (
          <Link
            to={`/workspaces/${project.get("id")}/settings/goals/new?key=Goals`}
            className="inline-flex items-center gap-x-1 cursor-pointer whitespace-nowrap hover:bg-gray-50 rounded px-2 py-1 transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            <span className="text-sm">{t("New Goal")}</span>
          </Link>
        );
      case "Settings":
        return (
          <SettingNav
            navItems={workspaceSettingNavItems(project.get("id"))}
            position="justify-end pr-2 md:pr-10"
          />
        );
      case "Inbox":
        return (
          <>
            {/* Mobile: Show Add New dropdown */}
            <div className="flex items-center space-x-2 md:hidden flex-shrink-0">
              {renderAddButton()}
              {renderInboxButton()}
              {renderUserProfile()}
            </div>
            {/* Desktop: Show separate Add New and New Activity buttons */}
            <div className="hidden md:flex items-center space-x-2 flex-shrink-0">
              {renderAddNewItem()}
              {renderNewActivityButton()}
              {renderInboxButton()}
              {renderUserProfile()}
            </div>
          </>
        );
      default:
        return (
          <>
            {/* Mobile: Show Add New dropdown */}
            <div className="flex items-center space-x-2 md:hidden flex-shrink-0">
              {renderAddButton()}
              {renderInboxButton()}
              {renderUserProfile()}
            </div>
            {/* Desktop: Show separate Add New and New Activity buttons */}
            <div className="hidden md:flex items-center space-x-2 flex-shrink-0">
              {renderAddNewItem()}
              {renderNewActivityButton()}
              {renderInboxButton()}
              {renderUserProfile()}
            </div>
          </>
        );
    }
  };

  return <AppBreadcrumb suffixItems={renderActions()} />;
};
export default TopNav;
